import { Module } from "../../module";
import { ModuleId, SUBJECT_TYPE_SEPERATOR, AskMessage, ServerRequestErrorType, ServerRequestErrorMessage, ByteStreamReader, MessageFactory, AskErrorMessage, UINT32_MAX_VALUE, ServerBufferMessageType, AskMessageErrorCode, SendToServerMessage } from "flavr-common";
import { serverConnection } from "./ServerConnectionService";
import { Timer, TimerType } from "../../../messages/timing";
import { UserAuthenticatedMessage } from "../../../messages/session/SessionMessages";
import { animationFrame$ } from "../../../messages/xr/AnimationFrame";
const loading = document.querySelector("#loading");
const FADE_OUT_LOADING_RETRY_TIME = 100;
// how long do we wait for a response from the server before we send a timeout error message
const MAX_SERVER_RESPONSE_TIME = 10 * 60 * 1000; // 10 min
/**
 * Bridges the local module bus to the backend server so client messages can be forwarded, server replies can be decoded and ask/answer lifecycles can be tracked safely.
 *
 * Messages:
 * - `SendToServerMessage` - Queues its wrapped messages to the server and flushes them immediately.
 * - Bus messages addressed to the `server` subject, including `AskMessage` - Forwarded to the backend; asks are tracked with a timeout so a `ServerRequestErrorMessage` is sent back if the connection is offline or the server never answers in time.
 * - `ServerArrayBufferMessage` (received outside the bus, over the raw server connection) - Decodes the buffered bus, answer or ask messages sent by the server and re-injects them into the local bus, answering pending asks or forwarding new ones.
 * - `UserAuthenticatedMessage` - Fades out the loading overlay once the server connection is confirmed online.
 */
export class ServerConnectionModule extends Module {
    /**
     * Subscribes to the messages needed to fade out the loading overlay, forward queued messages to the server, and
     * handle bus messages addressed to the `server` subject.
     */
    constructor() {
        super(ModuleId.SERVER_CONNECTION);
        this.offlineTimeout = new Timer();
        this.onOnlineCallbacks = [];
        this.onOfflineCallbacks = [];
        this.requestTimeouts = new Map();
        this.nextFreeAskId = 0;
        this.handleSendToServerMessage = (msg) => {
            const messages = msg.messages;
            for (const message of messages)
                serverConnection.queueBusMessage(message);
            serverConnection.flushPendingMessages();
        };
        this.fadeOutLoading = () => {
            if (loading === undefined || loading === null)
                return;
            if (serverConnection.isServerOnline()) {
                loading.classList.add("fadeout");
                loading.addEventListener("transitionend", () => {
                    loading.remove();
                });
                return;
            }
            setTimeout(this.fadeOutLoading, FADE_OUT_LOADING_RETRY_TIME);
        };
        /**
         * Flushes any bus messages queued for the server during this frame, logging (but swallowing) errors so a failed
         * flush doesn't break the render loop.
         */
        this.onLateUpdate = () => {
            try {
                serverConnection.flushPendingMessages();
            }
            catch (error) {
                console.error(error);
            }
        };
        this.handleServerMessage = (msg) => {
            switch (msg.channel) {
                case "session:arrayBuffer":
                    this.handleArrayBufferFromServer(msg);
                    break;
            }
        };
        this.connection.subscribeByType(UserAuthenticatedMessage, this.fadeOutLoading);
        this.connection.subscribeByType(SendToServerMessage, this.handleSendToServerMessage);
        this.connection.subscribe(["server" + SUBJECT_TYPE_SEPERATOR], (msg) => {
            this.handleMessage(msg);
        });
    }
    /**
     * Connects the module to the low-level server connection and registers the late-update flush that sends queued server messages.
     */
    init() {
        serverConnection.messageDelegate.add(this.handleServerMessage);
        animationFrame$.onLateUpdate$.add(this.onLateUpdate);
    }
    handleMessage(msg) {
        if (AskMessage.is(msg)) {
            this.waitForOnline(() => {
                this.handleAskMessage(msg);
            }, () => {
                this.connection.answer(msg.askId, new ServerRequestErrorMessage(ServerRequestErrorType.OFFLINE));
            });
        }
        else {
            serverConnection.queueBusMessage(msg);
        }
    }
    getNextFreeAskId() {
        const askId = this.nextFreeAskId;
        do {
            // We limit to Uint32
            if (this.nextFreeAskId >= UINT32_MAX_VALUE) {
                this.nextFreeAskId = 0;
            }
            else {
                ++this.nextFreeAskId;
            }
        } while (this.requestTimeouts.has(this.nextFreeAskId));
        return askId;
    }
    handleAskMessage(msg) {
        const askId = this.getNextFreeAskId();
        const timer = new Timer();
        this.requestTimeouts.set(askId, {
            timer,
            busAskId: msg.askId
        });
        timer.setup(() => {
            this.connection.answer(msg.askId, new ServerRequestErrorMessage(ServerRequestErrorType.TIMEOUT));
            this.requestTimeouts.delete(askId);
        }, MAX_SERVER_RESPONSE_TIME);
        serverConnection.queueAskMessage(msg, askId);
    }
    handleArrayBufferFromServer(data) {
        const buffer = data.payload;
        const stream = new ByteStreamReader(buffer);
        const msgType = stream.getUint8();
        switch (msgType) {
            case ServerBufferMessageType.BusMsg:
                this.handleBufferBusMessageFromServer(stream);
                break;
            case ServerBufferMessageType.AnswerMsg:
                this.handleBufferAnswerMessageFromServer(stream);
                break;
            case ServerBufferMessageType.AskMsg:
                this.handleBufferAskMessageFromServer(stream);
                break;
        }
    }
    getBusMessagesFromBuffer(stream) {
        const messages = [];
        let msgCount = 0;
        try {
            msgCount = stream.getUint32();
        }
        catch (error) {
            console.error("[multiplayer] dropping malformed server packet header", error);
            return;
        }
        for (let i = 0; i < msgCount; ++i) {
            let typeHash;
            try {
                typeHash = stream.getTypeHash();
                const msg = MessageFactory.create(typeHash);
                if (!msg.serialization) {
                    console.error("[multiplayer] serialization is not defined", msg.type);
                    return;
                }
                msg.serialization(stream);
                messages.push(msg);
            }
            catch (error) {
                console.error(`[multiplayer] dropping malformed server packet at message ${String(i + 1)}/${String(msgCount)} (typeHash=${String(typeHash)})`, error);
                return;
            }
        }
        return messages;
    }
    handleBufferBusMessageFromServer(stream) {
        const messages = this.getBusMessagesFromBuffer(stream);
        if (!messages)
            return;
        this.connection.processMessagesFromServer(messages);
    }
    handleBufferAskMessageFromServer(stream) {
        const askId = stream.getUint32();
        try {
            const typeHash = stream.getTypeHash();
            const msg = MessageFactory.create(typeHash);
            if (!msg.serialization) {
                console.error("[multiplayer] serialization is not defined", msg.type);
                throw AskMessageErrorCode.SerializationNotDefined;
            }
            msg.serialization(stream);
            this.connection.ask(msg, (msgs) => {
                try {
                    if (msgs.length !== 1) {
                        throw AskMessageErrorCode.MoreThanOneMessage;
                    }
                    const msg = msgs[0];
                    if (AskErrorMessage.is(msg)) {
                        throw AskMessageErrorCode.ClientAnswerWithError;
                    }
                    const buffer = serverConnection.packAnswerInArrayBuffer(msg, askId);
                    if (buffer === undefined) {
                        serverConnection.createAnswerErrorBuffer(askId, AskMessageErrorCode.CantCreateBuffer);
                        return;
                    }
                    serverConnection.sendArrayBuffer(buffer);
                }
                catch (error) {
                    let errorCode = 1;
                    if (typeof error === "number") {
                        errorCode = error;
                    }
                    const buffer = serverConnection.createAnswerErrorBuffer(askId, errorCode);
                    serverConnection.sendArrayBuffer(buffer);
                }
            });
        }
        catch (error) {
            let errorCode = AskMessageErrorCode.Unknown;
            if (typeof error === "number") {
                errorCode = error;
            }
            const buffer = serverConnection.createAnswerErrorBuffer(askId, errorCode);
            serverConnection.sendArrayBuffer(buffer);
        }
    }
    handleBufferAnswerMessageFromServer(stream) {
        const askId = stream.getUint32();
        const data = this.requestTimeouts.get(askId);
        if (data === undefined || !data.timer.isPlanned()) {
            console.warn("[multiplayer] recveived answer from server after time out timer has expired");
            return;
        }
        data.timer.cancel();
        this.requestTimeouts.delete(askId);
        const messages = this.getBusMessagesFromBuffer(stream);
        if (messages) {
            this.connection.sendMultiple(messages, data.busAskId);
        }
    }
    /**
     * Defers work until the server connection is online and falls back to the offline callback when the connection cannot be used.
     */
    waitForOnline(onOnline, onOffline) {
        if (!serverConnection.waitForServerInitData && serverConnection.isServerOnline()) {
            onOnline();
            return;
        }
        if (serverConnection.isServerOffline()) {
            onOffline();
            return;
        }
        let count = 0;
        this.onOnlineCallbacks.push(onOnline);
        this.onOfflineCallbacks.push(onOffline);
        if (!this.offlineTimeout.isPlanned()) {
            this.offlineTimeout.setup(() => {
                if (!serverConnection.waitForServerInitData) {
                    if (!serverConnection.isServerOffline() && ++count < 5) {
                        if (serverConnection.isServerOnline()) {
                            this.offlineTimeout.cancel();
                            for (const callback of this.onOnlineCallbacks)
                                callback();
                            this.onOnlineCallbacks = [];
                        }
                    }
                    else {
                        this.offlineTimeout.cancel();
                        for (const callback of this.onOfflineCallbacks)
                            callback();
                        this.onOfflineCallbacks = [];
                    }
                }
            }, 100, TimerType.Interval);
        }
    }
}
